// React
import { ReactElement, useCallback, useState } from 'react';
// Next
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
// Modules
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ListboxOption } from '@reach/listbox';
import { toast } from 'react-toastify';
// Design System
import { Button } from '@utmb/design-system/button/Button';
import { Checkbox } from '@utmb/design-system/checkbox/Checkbox';
import { Font } from '@utmb/design-system/text/font';
import { Radio } from '@utmb/design-system/radio/Radio';
import { Select, SELECT_DEFAULT_VALUE } from '@utmb/design-system/select/Select';
import { InputMessage } from '@utmb/design-system/input-message/InputMessage';
// Features
import { getKeycloakToken } from '@utmb/features/sso/keycloak';
import { MemberForm } from './MemberForm';
import { OrganizerForm } from './OrganizerForm';
// Types
import { ColorTypesEnum } from '@utmb/types/colors';
import { InputLanguage } from '@utmb/types/input-language';
import { MainLanguagesEnum } from '@utmb/types/main-languages-enum';
import UserCreateStatusView from '@utmb/types/view/UserCreateStatusView';
import { UserCreateStatusEnum } from '@utmb/types/enum/UserCreateStatusEnum';
import { RegistrationConfirmation, RegistrationYourQuest } from '@utmb/layouts/my-utmb/navigation';
import { UserAccountTypeEnum } from '@utmb/types/enum/UserAccountTypeEnum';
// Utils
import { getColorStyle } from '@utmb/utils/colors';
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
// Styles
import styles from './user-information.module.scss';
const LOCAL_STORAGE_TOUR = 'dashboardTourDisplayed';

export const UserInformation = (): ReactElement => {
    const [showFallbackLanguage, setShowFallbackLanguage] = useState(false);
    const [canSubmit, setCanSubmit] = useState(true);
    const [isMemberView, setIsMemberView] = useState(true);

    const { t } = useTranslation();
    const router = useRouter();

    const { redirect_uri } = router.query;

    const currentYear = new Date().getFullYear();

    if (localStorage.getItem(LOCAL_STORAGE_TOUR) === 'true') localStorage.setItem(LOCAL_STORAGE_TOUR, 'false');

    const languages: InputLanguage[] =
        router.locales?.map((locale) => ({
            value: locale.toUpperCase(),
            label: t(`common.languages.${locale}`),
        })) || [];

    const fallbackLanguages: InputLanguage[] =
        Object.values(MainLanguagesEnum)
            .filter((value) => typeof value === 'string')
            .map((locale) => {
                const lowerCaseLocale = (locale as string).toLowerCase();

                return {
                    value: locale as string,
                    label: t(`common.languages.${lowerCaseLocale}`),
                };
            }) || [];

    const noInputError = t('contactForm.noInputError');

    const validationSchemaMember = {
        gender: Yup.string()
            .max(255, t('form.lengthExceededError', { length: 255 }))
            .required(noInputError),
        firstName: Yup.string()
            .max(255, t('form.lengthExceededError', { length: 255 }))
            .required(noInputError),
        lastName: Yup.string()
            .max(255, t('form.lengthExceededError', { length: 255 }))
            .required(noInputError),
        birthdate: Yup.string()
            .length(10, t('form.lengthExceededError', { length: 10 }))
            .required(noInputError)
            .test('age-range', t('form.ageOutOfRangeError', { min: 10, max: 99 }), (value) => {
                if (!value) return false;

                const birthYear = new Date(value).getFullYear();
                const minYear = currentYear - 99;
                const maxYear = currentYear - 10;
                return birthYear >= minYear && birthYear <= maxYear;
            }),
        nationality: Yup.string()
            .length(2, t('form.countryNotSelectedError', { length: 2 }))
            .required(noInputError),
    };

    const validationSchemaOrganizer = {
        name: Yup.string()
            .max(255, t('form.lengthExceededError', { length: 255 }))
            .required(noInputError),
        city: Yup.string()
            .max(255, t('form.lengthExceededError', { length: 255 }))
            .required(noInputError),
        countryCode: Yup.string()
            .max(2, t('form.lengthExceededError', { length: 2 }))
            .required(noInputError),
        phone: Yup.string()
            .max(255, t('form.lengthExceededError', { length: 255 }))
            .required(noInputError),
    };

    const formik = useFormik({
        initialValues: {
            type: UserAccountTypeEnum.MEMBER,

            // Member properties
            gender: '',
            firstName: '',
            lastName: '',
            birthdate: '',
            nationality: '',

            // Organizer properties
            name: '',
            city: '',
            countryCode: '',
            phone: '',

            // Shared properties
            preferredLanguage: '',
            fallbackLanguage: '',
            optinWorldSeries: false,
            optinUtmbLive: false,
            optinIndex: false,
            acceptTos: false,
        },
        validationSchema: Yup.object({
            ...(isMemberView ? validationSchemaMember : validationSchemaOrganizer),
            preferredLanguage: Yup.string().notOneOf(['', 'default'], noInputError).required(noInputError),
            fallbackLanguage: Yup.string().when('preferredLanguage', {
                is: (preferredLanguage: string) => {
                    return (
                        preferredLanguage &&
                        !Object.values(MainLanguagesEnum)
                            .map((l) => l.toString())
                            .includes(preferredLanguage.toLowerCase())
                    );
                },
                then: (schema) => schema.notOneOf(['', 'default'], noInputError).required(noInputError),
                otherwise: (schema) => schema.notRequired(),
            }),
            optinWorldSeries: Yup.boolean().required(noInputError),
            optinUtmbLive: Yup.boolean().required(noInputError),
            optinIndex: Yup.boolean().required(noInputError),
            acceptTos: Yup.boolean().oneOf([true], t('myutmb:myUtmb.registration.tosRequired')).required(noInputError),
        }),
        onSubmit: async (values) => {
            const token = await getKeycloakToken();

            const response = (await fetchTheAPI({
                isClient: true,
                path: isMemberView ? '/users/dashboard/member/create' : '/users/dashboard/organizer/create',
                method: FetchMethodsEnum.POST,
                bearer: token,
                body: values,
            })) as UserCreateStatusView;

            setCanSubmit(false);

            if (response.status === UserCreateStatusEnum.OK) {
                if (redirect_uri) {
                    window.location.href = `${redirect_uri}`;
                } else router.push(isMemberView ? RegistrationYourQuest.slug : RegistrationConfirmation.slug);
            } else {
                toast.error(t('myutmb:myUtmb.registration.accountExists'));

                setTimeout(() => {
                    if (redirect_uri) {
                        window.location.href = `${redirect_uri}`;
                    } else router.push('/my-utmb/my-dashboard');
                }, 2000);
            }
        },
    });

    const typeHandleChange = useCallback(
        (value: any) => {
            if (value?.target?.value) {
                formik.getFieldHelpers('type').setValue(value.target.value);
                setIsMemberView(value.target.value === UserAccountTypeEnum.MEMBER);
            }
        },
        [formik]
    );

    return (
        <div className={styles.container}>
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        <Font.FuturaMedium className={styles.title} desktop="32" as="h1">
                            {t('myutmb:myUtmb.registration.title')}
                        </Font.FuturaMedium>
                        <Font.FuturaBook className={styles.subtitle} desktop="18" as="h2">
                            {t('myutmb:myUtmb.registration.subtitle')}
                        </Font.FuturaBook>
                    </div>
                </div>
            </div>

            <form onSubmit={formik.handleSubmit} className={styles.form}>
                <div className={`${styles.form_container} container`}>
                    <div className={`${styles.row} row`}>
                        <div className="col-12">
                            <Font.OxaniumRegular className={styles.label} mobile="12" desktop="12">
                                {t('myutmb:myUtmb.registration.type.title')} *
                            </Font.OxaniumRegular>
                            <ul className={styles.radio_list}>
                                <li className={styles.radio_item_container}>
                                    <Radio
                                        id="typeMember"
                                        name="type"
                                        label={t('myutmb:myUtmb.registration.member')}
                                        value={UserAccountTypeEnum.MEMBER}
                                        onChange={typeHandleChange}
                                        onBlur={typeHandleChange}
                                        isChecked
                                    />
                                </li>
                                <li className={styles.radio_item_container}>
                                    <Radio
                                        id="typeOrganizer"
                                        name="type"
                                        label={t('myutmb:myUtmb.registration.organizer')}
                                        value={UserAccountTypeEnum.ORGANIZER}
                                        onChange={typeHandleChange}
                                        onBlur={typeHandleChange}
                                    />
                                </li>
                            </ul>
                        </div>
                    </div>

                    {isMemberView ? <MemberForm formik={formik} /> : <OrganizerForm formik={formik} />}

                    <div className={`${styles.row} row`}>
                        <div className="col-12 col-md-6">
                            <Select
                                id="preferredLanguage"
                                name="preferredLanguage"
                                label={t('myutmb:myUtmb.myInformation.basicInformation.communicationLanguage')}
                                firstOptionlabel={t('common.selectLanguage')}
                                onchange={(value: string) => {
                                    formik.getFieldHelpers('preferredLanguage').setValue(value);
                                    const isNotMainLanguages = value !== SELECT_DEFAULT_VALUE && !(value.toUpperCase() in MainLanguagesEnum);
                                    setShowFallbackLanguage(isNotMainLanguages);
                                    if (!isNotMainLanguages) {
                                        formik.getFieldHelpers('fallbackLanguage').setValue('');
                                    }
                                }}
                                value={formik.values.preferredLanguage}
                                require={true}
                            >
                                {languages.map((language: InputLanguage, key: number) => (
                                    <ListboxOption key={`communicationLanguage_${key}`} value={language.value} label={language.label}>
                                        <div className={styles.language_container}>
                                            <Font.FuturaBook as="span" mobile="14" desktop="16">
                                                {language.label}
                                            </Font.FuturaBook>
                                        </div>
                                    </ListboxOption>
                                ))}
                            </Select>
                            {formik.touched.preferredLanguage && formik.errors.preferredLanguage && (
                                <InputMessage type="error" message={formik.errors.preferredLanguage} />
                            )}
                        </div>
                        {showFallbackLanguage && (
                            <div className="col-12 col-md-6">
                                <Select
                                    id="fallbackLanguage"
                                    name="fallbackLanguage"
                                    label={t('myutmb:myUtmb.registration.otherSpokenLanguages')}
                                    firstOptionlabel={t('common.selectLanguage')}
                                    onchange={(value: string) => {
                                        formik.getFieldHelpers('fallbackLanguage').setValue(value);
                                    }}
                                    value={formik.values.fallbackLanguage}
                                    require={true}
                                >
                                    {fallbackLanguages.map((language: InputLanguage, key: number) => (
                                        <ListboxOption key={`fallbackLanguage_${key}`} value={language.value} label={language.label}>
                                            <div className={styles.language_container}>
                                                <Font.FuturaBook as="span" mobile="14" desktop="16">
                                                    {language.label}
                                                </Font.FuturaBook>
                                            </div>
                                        </ListboxOption>
                                    ))}
                                </Select>
                                {formik.touched.fallbackLanguage && formik.errors.fallbackLanguage && (
                                    <InputMessage type="error" message={formik.errors.fallbackLanguage} />
                                )}
                            </div>
                        )}
                    </div>

                    <div className={styles.newsletter}>
                        <Font.OxaniumRegular className={`${styles.label} ${styles.label_newsletter}`} mobile="16" desktop="18">
                            {t('common.newsletters')}
                        </Font.OxaniumRegular>

                        <div className={[styles.newsletter_element, styles.checkbox].join(' ')}>
                            <Checkbox
                                id="optinWorldSeries"
                                name="optinWorldSeries"
                                checked={formik.values.optinWorldSeries}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                label={t('myutmb:myUtmb.registration.optinWorldSeries')}
                            />
                        </div>

                        <div className={[styles.newsletter_element, styles.checkbox].join(' ')}>
                            <Checkbox
                                id="optinUtmbLive"
                                name="optinUtmbLive"
                                checked={formik.values.optinUtmbLive}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                label={t('myutmb:myUtmb.registration.optinUtmbLive')}
                            />
                        </div>

                        <div className={[styles.newsletter_element, styles.checkbox].join(' ')}>
                            <Checkbox
                                id="optinIndex"
                                name="optinIndex"
                                checked={formik.values.optinIndex}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                label={t('myutmb:myUtmb.registration.optinIndex')}
                            />
                        </div>
                    </div>

                    <div className={styles.newsletter}>
                        <Font.OxaniumRegular className={`${styles.label} ${styles.label_newsletter}`} mobile="16" desktop="18">
                            {t('myutmb:myUtmb.registration.tosTitle')}
                        </Font.OxaniumRegular>

                        <div className={[styles.newsletter_element, styles.checkbox].join(' ')}>
                            <Checkbox
                                id="acceptTos"
                                name="acceptTos"
                                checked={formik.values.acceptTos}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                label={t('myutmb:myUtmb.registration.tosCheckbox')}
                            />
                        </div>
                        {formik.touched.acceptTos && formik.errors.acceptTos && <InputMessage type="error" message={formik.errors.acceptTos} />}

                        <Font.FuturaBook mobile="16" desktop="18" as="div" className={styles.newsletter_element}>
                            <span dangerouslySetInnerHTML={{ __html: t('myutmb:myUtmb.registration.gdprText') }} />
                        </Font.FuturaBook>
                    </div>

                    <Button.Default
                        className={styles.form_submit_button}
                        type="submit"
                        label={t('myutmb:myUtmb.registration.submitButton.label')}
                        color={getColorStyle(ColorTypesEnum.WS_SECONDARY)}
                        bgcolor={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                        disabled={formik.isSubmitting || !canSubmit || !formik.isValid}
                    ></Button.Default>
                </div>
            </form>
        </div>
    );
};
